/**
 * UsageByModelChart Component
 *
 * Displays a bar chart showing token usage breakdown by AI model.
 * Uses simple CSS-based bars for visualization (no external chart library required).
 *
 * Technical Design Reference: docs/technical/TD-AC-594-token-audit.md
 * Related Tickets:
 * - AC-594 (Epic: Token Audit System)
 * - AC-607 (Frontend: UsageByModelChart component)
 * - AC-612 (Parent: UsageDashboard container)
 */

import React from 'react';
import { Cpu, AlertCircle } from 'lucide-react';
import type { TokenUsageByModel } from '../../types/tokenUsage';

export interface UsageByModelChartProps {
  /** Token usage by model data from API */
  data: TokenUsageByModel[] | null;
  /** Whether data is currently loading */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
}

/**
 * Format a number with thousands separator using locale formatting.
 */
const formatNumber = (n: number): string => {
  return n.toLocaleString('en-US');
};

/**
 * Format a number as currency with $ symbol.
 * Uses adaptive precision: 4 decimal places for amounts under $1,
 * 2 decimal places for larger amounts.
 */
const formatCurrency = (n: number): string => {
  const decimalPlaces = n < 1 && n > 0 ? 4 : 2;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
};

/**
 * Get a color class for a model based on its provider and index.
 */
const getModelColor = (provider: string, index: number): string => {
  const providerColors: Record<string, string[]> = {
    openai: ['bg-green-500', 'bg-green-400', 'bg-green-600', 'bg-emerald-500'],
    anthropic: ['bg-orange-500', 'bg-orange-400', 'bg-amber-500', 'bg-orange-600'],
    google: ['bg-blue-500', 'bg-blue-400', 'bg-indigo-500', 'bg-primary-600'],
    other: ['bg-gray-500', 'bg-gray-400', 'bg-slate-500', 'bg-gray-600'],
  };

  const colors = providerColors[provider.toLowerCase()] || providerColors.other;
  return colors[index % colors.length];
};

/**
 * Get provider display name with capitalization.
 */
const getProviderDisplayName = (provider: string): string => {
  const displayNames: Record<string, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google',
  };
  return displayNames[provider.toLowerCase()] || provider;
};

/**
 * Loading skeleton for the chart.
 */
const LoadingSkeleton: React.FC = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label="Loading model usage data"
    data-testid="model-chart-loading"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 animate-pulse"
  >
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-4" />
    <div className="space-y-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex items-center space-x-4">
          <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="flex-1 h-6 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="w-16 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Error state component.
 */
const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    data-testid="model-chart-error"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6"
  >
    <div className="flex items-center text-red-600 mb-4">
      <AlertCircle data-testid="model-error-icon" className="w-5 h-5 mr-2" />
      <span className="font-medium">Error loading model data</span>
    </div>
    <p className="text-gray-600 dark:text-gray-400">{message}</p>
  </div>
);

/**
 * Empty state when no data is available.
 */
const EmptyState: React.FC = () => (
  <div
    data-testid="model-chart-empty"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 text-center"
  >
    <Cpu className="w-12 h-12 text-gray-400 mx-auto mb-3" />
    <p className="text-gray-500 dark:text-gray-400">No model usage data available</p>
    <p className="text-sm text-gray-400 mt-1">Data will appear once AI models are used</p>
  </div>
);

/**
 * Calculate the maximum value for scaling the bars.
 */
const getMaxTokens = (data: TokenUsageByModel[]): number => {
  if (data.length === 0) return 1;
  const max = Math.max(...data.map(d => d.total_tokens));
  return max === 0 ? 1 : max;
};

/**
 * UsageByModelChart component displays token usage breakdown by AI model.
 */
const UsageByModelChart: React.FC<UsageByModelChartProps> = ({
  data,
  loading = false,
  error = null,
}) => {
  // Show loading state
  if (loading) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error) {
    return <ErrorState message={error} />;
  }

  // Show empty state when no data
  if (!data || data.length === 0) {
    return <EmptyState />;
  }

  const maxTokens = getMaxTokens(data);

  // Sort by total_tokens descending
  const sortedData = [...data].sort((a, b) => b.total_tokens - a.total_tokens);

  return (
    <div
      data-testid="usage-by-model-chart"
      className="bg-white dark:bg-cap-dark rounded-lg shadow p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Cpu data-testid="model-chart-icon" className="w-5 h-5 text-primary-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">Usage by Model</h3>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">{data.length} model{data.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Bar Chart */}
      <div data-testid="model-chart-bars" className="space-y-4">
        {sortedData.map((model, index) => {
          const barWidth = (model.total_tokens / maxTokens) * 100;
          const colorClass = getModelColor(model.provider, index);

          return (
            <div
              key={`${model.provider}-${model.model}`}
              data-testid={`model-bar-${index}`}
              className="group"
            >
              {/* Model info row */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-900 dark:text-warm-white">{model.model}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                    {getProviderDisplayName(model.provider)}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-gray-500 dark:text-gray-400">{model.api_call_count} calls</span>
                  <span className="font-medium text-gray-900 dark:text-warm-white">{formatCurrency(model.total_cost)}</span>
                </div>
              </div>

              {/* Bar */}
              <div className="h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${colorClass} rounded-full transition-all duration-300 group-hover:opacity-80 flex items-center`}
                  style={{ width: `${barWidth}%` }}
                >
                  <span className="text-xs text-white font-medium ml-2 truncate">
                    {formatNumber(model.total_tokens)} tokens ({model.percentage_of_total.toFixed(1)}%)
                  </span>
                </div>
              </div>

              {/* Token breakdown */}
              <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500 dark:text-gray-400">
                <span>Prompt: {formatNumber(model.prompt_tokens)}</span>
                <span>Completion: {formatNumber(model.completion_tokens)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div
        data-testid="model-chart-summary"
        className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-3 gap-4 text-sm"
      >
        <div>
          <span className="text-gray-500 dark:text-gray-400">Total Tokens:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">
            {formatNumber(data.reduce((sum, d) => sum + d.total_tokens, 0))}
          </span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Total Cost:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">
            {formatCurrency(data.reduce((sum, d) => sum + d.total_cost, 0))}
          </span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-gray-400">Total Calls:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-warm-white">
            {formatNumber(data.reduce((sum, d) => sum + d.api_call_count, 0))}
          </span>
        </div>
      </div>
    </div>
  );
};

export default UsageByModelChart;
